import React, { useEffect, useMemo, useState } from 'react';
import { Form, Radio } from 'antd';
import useQueryApiClient, { RequestMethod } from 'utils/useApiClient';
import { useT } from 'hooks/Locale/useT';
import { constructValidationRules } from './validations';
export interface OptionType {
    value: string | number;
    label: React.ReactNode;
}

const RadioGroupWithLabelTarget = React.forwardRef<any, any>(({ id, children, ...rest }, ref) => {
    let idAssigned = false;
    const mapped = React.Children.map(children, (child) => {
        if (!idAssigned && React.isValidElement(child)) {
            idAssigned = true;
            return React.cloneElement(child as React.ReactElement<any>, { id });
        }
        return child;
    });

    return (
        <Radio.Group ref={ref} {...rest} {...(idAssigned ? {} : { id })}>
            {mapped}
        </Radio.Group>
    );
});

export interface SimpleRadioProps extends React.ComponentProps<typeof Radio.Group> {
    options?: OptionType[];
    isFormItem?: boolean;
    name?: string;
    label?: string;
    rules?: any[];
    className?: string;
    validation?: Record<string, any>;
    customLayout?: { labelCol?: object; wrapperCol?: object };
    url?: string;
    params?: Record<string, any>;
    dataKey?: string;
    valueKey?: string;
    labelKey?: string;
    method?: RequestMethod;
    customRules?: any[];
    translation?: string;
}

const SimpleRadio: React.FC<SimpleRadioProps> = ({
    options,
    isFormItem = true,
    name,
    label,
    className,
    validation = {},
    customRules,
    url,
    customLayout,
    params,
    dataKey,
    valueKey = 'value',
    labelKey = 'label',
    method = 'GET',
    translation = '',
    ...groupProps
}) => {
    const t = useT();

    let labelCol: any = { span: 8 };
    let wrapperCol: any = { span: 6 };

    if (customLayout) {
        labelCol = customLayout.labelCol ?? undefined;
        wrapperCol = customLayout.wrapperCol ?? undefined;
    }

    const [internalOptions, setInternalOptions] = useState<OptionType[]>(options ?? []);

    useEffect(() => {
        if (options) setInternalOptions(options);
    }, [options]);

    const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);
    const shouldFetch = !options && !!url;

    const { fetch, isFetching } = useQueryApiClient({
        request: {
            url: url ?? '',
            method,
            data: params ?? {},
            disableOnMount: true,
        },
        onSuccess: (response): void => {
            const raw = dataKey ? response?.[dataKey] : response;
            let arr: OptionType[] = [];
            if (Array.isArray(raw)) {
                arr = raw.map((item: any) => ({
                    value: item?.[valueKey],
                    label: item?.[labelKey],
                }));
            } else if (raw && typeof raw === 'object') {
                arr = Object.entries(raw).map(([k, v]) => ({ value: k, label: v as any }));
            }
            setInternalOptions(arr);
        },
    });

    useEffect(() => {
        if (!shouldFetch) return;
        fetch(params ?? {});
    }, [shouldFetch, url, method, dataKey, paramsKey]);

    const translationLabel = (value: string): string => {
        if (translation) {
            return t(`${translation}.${value.toLowerCase()}`);
        }
        return value;
    };

    const group = (
        <RadioGroupWithLabelTarget
            className={className}
            disabled={groupProps.disabled || isFetching}
            {...groupProps}
        >
            {internalOptions.map((opt: any) => (
                <Radio key={String(opt.value)} value={opt.value}>
                    {translationLabel(opt.label)}
                </Radio>
            ))}
        </RadioGroupWithLabelTarget>
    );

    if (!isFormItem) return group;

    return (
        <Form.Item
            name={name}
            label={t(label!)}
            labelCol={labelCol}
            rules={customRules || constructValidationRules(validation, t)}
            wrapperCol={wrapperCol}
        >
            {group}
        </Form.Item>
    );
};

export default SimpleRadio;
